<script lang="ts">
import { ref } from 'vue'
import 'vue-pdf-embed/dist/styles/annotationLayer.css'
import 'vue-pdf-embed/dist/styles/textLayer.css'

import { useDesignStore } from '@/stores/designStore.ts'
import { BTableSimple, BTbody, BTd, BTh, BThead, BTr } from 'bootstrap-vue-next'
import HeaderTextFancy from '@/components/HeaderTextFancy.vue'

export default {
  props: ['title'],
  setup(props, context) {
    const modal = ref(false)

    const designStore = useDesignStore()
    function lightenDarkenColor(col, amt) {
      var num = parseInt(col.substring(1), 16)
      var r = (num >> 16) + amt
      var b = ((num >> 8) & 0x00ff) + amt
      var g = (num & 0x0000ff) + amt
      var newColor = g | (b << 8) | (r << 16)
      return '#' + newColor.toString(16)
    }
    return {
      designStore,
      props,
      lightenDarkenColor
    }
  },
  components: {
    BTableSimple,
    BThead,
    BTr,
    BTh,
    BTbody,
    BTd,
    HeaderTextFancy
  }
}
</script>

<template>
  <div
    :style="{
      '--bs-table-bg': designStore.inputBacking,
      '--bs-table-color': designStore.inputText,
      '--bs-table-border-color': designStore.secondaryTheme,
      '--bs-table-hover-bg': lightenDarkenColor(designStore.inputBacking, 10),
      '--bs-table-hover-color': lightenDarkenColor(designStore.inputBacking, 10),
      '--hover-table': lightenDarkenColor(designStore.inputBacking, -20),
      '--table-bg': designStore.inputBacking,
      background: designStore.inputBacking
    }"
    style="padding: 1rem"
  >
    &#9;These are all of the basic weapons available in the game. Each weapon has damage dice, a
    damage type, reach/range, a STR requirement, # of hands, a Combat Style, and a Specialization.
    The damage dice is what is rolled to determine how much damage it deals, a basic <i>strike</i>.
    Reach is the maximum distance a melee attack can be made from. Range is the maximum distance a
    ranged attack can be made from without taking on range penalties. STR requirement is how much
    <i>Strength</i> a character needs to be able to weild the weapon with the specified # of hands.
    If the weapon requires only one hand, you may choose to use both hands to hold the weapon in
    order to halve the STR requrement. If you cannot meet the STR requirement of a weapon, you
    cannot make an attack with the weapon. The Combat Style and Specialization are the weapon skills
    that a player can spend MP on while weilding the wepaon. <br />
    <br /><HeaderTextFancy title="Notes"></HeaderTextFancy>
    <li>❖ Weapons can be upgraded through the <i>Crafting</i> skill.</li>
    <li>
      ❖ The <i>Might</i> skill can potentially decrease the #of Hands needed to wield a weapon for
      weapons whose # of Hands does not have a *.
    </li>
    <li>
      ❖ Weapons with casting in the name can channel spellcasting freely and do not take up the
      character's hand(s) for the purposes of spellcasting
    </li>
    <BTableSimple hover small caption-top responsive style="margin-top: 1rem">
      <caption
        style="padding: 1rem"
        :style="{
          background: designStore.primaryTheme,
          color: designStore.primaryText
        }"
      >
        Weapon List
      </caption>
      <BThead
        :style="{
          background: designStore.sidebarBacking,
          color: designStore.sidebarText,
          '--bs-table-bg': designStore.sidebarBacking,
          '--bs-table-color': designStore.sidebarText,
          '--table-bg': designStore.sidebarBacking
        }"
      >
        <BTr>
          <BTh>Name</BTh>
          <BTh>Damage Dice</BTh>
          <BTh>Damage Type</BTh>
          <BTh># of Hands</BTh>
          <BTh>Reach/Range</BTh>
          <BTh>STR Req.</BTh>
          <BTh>Combat Style</BTh>
          <BTh>Specialization</BTh>
        </BTr>
      </BThead>
      <BTbody>
        <BTr>
          <BTd>War Axe</BTd>
          <BTd class="smol">1d12</BTd>
          <BTd>Edged</BTd>
          <BTd class="smol">1</BTd>
          <BTd class="smol">5'</BTd>
          <BTd class="smol">6</BTd>
          <BTd>Versatile</BTd>
          <BTd>Axes</BTd>
        </BTr>
        <BTr>
          <BTd>Battle Axe</BTd>
          <BTd class="smol">2d8</BTd>
          <BTd>Edged</BTd>
          <BTd class="smol">2</BTd>
          <BTd class="smol">5'</BTd>
          <BTd class="smol">6</BTd>
          <BTd>Heavy</BTd>
          <BTd>Axes</BTd>
        </BTr>
        <BTr>
          <BTd>Great Axe</BTd>
          <BTd class="smol">1d20</BTd>
          <BTd>Edged</BTd>
          <BTd class="smol">2</BTd>
          <BTd class="smol">5'</BTd>
          <BTd class="smol">9</BTd>
          <BTd>Heavy</BTd>
          <BTd>Axes</BTd>
        </BTr>
        <BTr>
          <BTd>Longbow</BTd>
          <BTd class="smol">1d10</BTd>
          <BTd>Piercing</BTd>
          <BTd class="smol">2*</BTd>
          <BTd class="smol">80'</BTd>
          <BTd class="smol">4</BTd>
          <BTd>Versatile</BTd>
          <BTd>Bows</BTd>
        </BTr>
        <BTr>
          <BTd>Crossbow</BTd>
          <BTd class="smol">2d8</BTd>
          <BTd>Piercing</BTd>
          <BTd class="smol">2*</BTd>
          <BTd class="smol">80'</BTd>
          <BTd class="smol">6</BTd>
          <BTd>Heavy</BTd>
          <BTd>Crossbows</BTd>
        </BTr>
        <BTr>
          <BTd>Cestus</BTd>
          <BTd class="smol">1d12</BTd>
          <BTd>Piercing</BTd>
          <BTd class="smol">1</BTd>
          <BTd class="smol">5'</BTd>
          <BTd class="smol">6</BTd>
          <BTd>Versatile</BTd>
          <BTd>Brawling</BTd>
        </BTr>
        <BTr>
          <BTd>Pistol</BTd>
          <BTd class="smol">1d4</BTd>
          <BTd>Piercing</BTd>
          <BTd class="smol">1</BTd>
          <BTd class="smol">40'</BTd>
          <BTd class="smol">2</BTd>
          <BTd>Light</BTd>
          <BTd>Handguns</BTd>
        </BTr>
        <BTr>
          <BTd>SMG</BTd>
          <BTd class="smol">1d4</BTd>
          <BTd>Piercing</BTd>
          <BTd class="smol">1</BTd>
          <BTd class="smol">40'</BTd>
          <BTd class="smol">2</BTd>
          <BTd>Light</BTd>
          <BTd>Machine Guns</BTd>
        </BTr>
        <BTr>
          <BTd>Machine Gun</BTd>
          <BTd class="smol">1d10</BTd>
          <BTd>Piercing</BTd>
          <BTd class="smol">2*</BTd>
          <BTd class="smol">40'</BTd>
          <BTd class="smol">4</BTd>
          <BTd>Versatile</BTd>
          <BTd>Machine Guns</BTd>
        </BTr>
        <BTr>
          <BTd>War Hammer</BTd>
          <BTd class="smol">1d12</BTd>
          <BTd>Blunt</BTd>
          <BTd class="smol">1</BTd>
          <BTd class="smol">5'</BTd>
          <BTd class="smol">6</BTd>
          <BTd>Versatile</BTd>
          <BTd>Hammer</BTd>
        </BTr>
        <BTr>
          <BTd>Maul</BTd>
          <BTd class="smol">2d8</BTd>
          <BTd>Blunt</BTd>
          <BTd class="smol">2</BTd>
          <BTd class="smol">5'</BTd>
          <BTd class="smol">6</BTd>
          <BTd>Heavy</BTd>
          <BTd>Hammer</BTd>
        </BTr>
        <BTr>
          <BTd>Giant Hammer</BTd>
          <BTd class="smol">1d20</BTd>
          <BTd>Blunt</BTd>
          <BTd class="smol">2</BTd>
          <BTd class="smol">5'</BTd>
          <BTd class="smol">9</BTd>
          <BTd>Heavy</BTd>
          <BTd>Hammer</BTd>
        </BTr>
        <BTr>
          <BTd>Dagger</BTd>
          <BTd class="smol">1d6</BTd>
          <BTd>Piercing</BTd>
          <BTd class="smol">1</BTd>
          <BTd class="smol">5'</BTd>
          <BTd class="smol">2</BTd>
          <BTd>Light</BTd>
          <BTd>Knives</BTd>
        </BTr>
        <BTr>
          <BTd>Spear</BTd>
          <BTd class="smol">1d12</BTd>
          <BTd>Piercing</BTd>
          <BTd class="smol">1</BTd>
          <BTd class="smol">10'</BTd>
          <BTd class="smol">6</BTd>
          <BTd>Versatile</BTd>
          <BTd>Polearms</BTd>
        </BTr>
        <BTr>
          <BTd>Glaive</BTd>
          <BTd class="smol">2d8</BTd>
          <BTd>Piercing</BTd>
          <BTd class="smol">2</BTd>
          <BTd class="smol">10'</BTd>
          <BTd class="smol">6</BTd>
          <BTd>Heavy</BTd>
          <BTd>Polearms</BTd>
        </BTr>
        <BTr>
          <BTd>Sarissa</BTd>
          <BTd class="smol">1d20</BTd>
          <BTd>Piercing</BTd>
          <BTd class="smol">2</BTd>
          <BTd class="smol">10'</BTd>
          <BTd class="smol">9</BTd>
          <BTd>Heavy</BTd>
          <BTd>Polearms</BTd>
        </BTr>
        <BTr>
          <BTd>Carbine</BTd>
          <BTd class="smol">1d10</BTd>
          <BTd>Piercing</BTd>
          <BTd class="smol">2*</BTd>
          <BTd class="smol">60'</BTd>
          <BTd class="smol">4</BTd>
          <BTd>Versatile</BTd>
          <BTd>Rifle</BTd>
        </BTr>
        <BTr>
          <BTd>Long Gun</BTd>
          <BTd class="smol">2d8</BTd>
          <BTd>Piercing</BTd>
          <BTd class="smol">2*</BTd>
          <BTd class="smol">80'</BTd>
          <BTd class="smol">6</BTd>
          <BTd>Heavy</BTd>
          <BTd>Rifle</BTd>
        </BTr>
        <BTr>
          <BTd>Shotgun</BTd>
          <BTd class="smol">2d8</BTd>
          <BTd>Blunt</BTd>
          <BTd class="smol">2*</BTd>
          <BTd class="smol">20'</BTd>
          <BTd class="smol">6</BTd>
          <BTd>Heavy</BTd>
          <BTd>Shotguns</BTd>
        </BTr>
        <BTr>
          <BTd>Longsword</BTd>
          <BTd class="smol">1d12</BTd>
          <BTd>Edged</BTd>
          <BTd class="smol">1</BTd>
          <BTd class="smol">5'</BTd>
          <BTd class="smol">6</BTd>
          <BTd>Versatile</BTd>
          <BTd>Swords</BTd>
        </BTr>
        <BTr>
          <BTd>Bastard Sword</BTd>
          <BTd class="smol">2d8</BTd>
          <BTd>Edged</BTd>
          <BTd class="smol">2</BTd>
          <BTd class="smol">5'</BTd>
          <BTd class="smol">6</BTd>
          <BTd>Heavy</BTd>
          <BTd>Swords</BTd>
        </BTr>
        <BTr>
          <BTd>Great Sword</BTd>
          <BTd class="smol">1d20</BTd>
          <BTd>Edged</BTd>
          <BTd class="smol">2</BTd>
          <BTd class="smol">5'</BTd>
          <BTd class="smol">9</BTd>
          <BTd>Heavy</BTd>
          <BTd>Swords</BTd>
        </BTr>
        <BTr>
          <BTd>Lash</BTd>
          <BTd class="smol">1d6</BTd>
          <BTd>Edged</BTd>
          <BTd class="smol">1</BTd>
          <BTd class="smol">20''</BTd>
          <BTd class="smol">2</BTd>
          <BTd>Light</BTd>
          <BTd>Whips</BTd>
        </BTr>
        <BTr>
          <BTd>Lash</BTd>
          <BTd class="smol">1d12</BTd>
          <BTd>Edged</BTd>
          <BTd class="smol">1</BTd>
          <BTd class="smol">20''</BTd>
          <BTd class="smol">6</BTd>
          <BTd>Versatile</BTd>
          <BTd>Whips</BTd>
        </BTr>
        <BTr>
          <BTd>Casting Wand</BTd>
          <BTd class="smol">1d4</BTd>
          <BTd>Pierding</BTd>
          <BTd class="smol">1</BTd>
          <BTd class="smol">5''</BTd>
          <BTd class="smol">2</BTd>
          <BTd>Light</BTd>
          <BTd>Knives</BTd>
        </BTr>
        <BTr>
          <BTd>Casting Staff</BTd>
          <BTd class="smol">1d10</BTd>
          <BTd>Blunt</BTd>
          <BTd class="smol">2</BTd>
          <BTd class="smol">10'</BTd>
          <BTd class="smol">6</BTd>
          <BTd>Versatile</BTd>
          <BTd>Polearms</BTd>
        </BTr>
      </BTbody>
    </BTableSimple>
  </div>
</template>

<style scoped>
tr {
  --bs-table-hover-color: color;
  --bs-table-hover-bg: var(--hover-table);
  --bs-table-bg: var(--table-bg);
  --bs-table-color: color;
}
table {
  background-color: var(--table-bg);
}
td {
  --bs-table-bg: var(--table-bg);
  --bs-table-color: color;
}
.rulesReferenceContainer {
  display: flex;
}
.smol {
  text-align: center;
}
th {
  align-self: center;
  text-align: center;
}
li {
  padding-left: 1rem;
  margin-bottom: 0.25rem;
  list-style-type: none;
  text-indent: -1rem;
}
</style>
